import React, { useState, useEffect } from "react";
import { Checkbox, Select, Space, Typography, Collapse } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useGeneralReportTrackingEvent } from "../hooks/useGeneralReportTrackingEvent";
import {
  ENROLLMENT_CLAIMS_TYPE,
  enrollmentDateTypes,
  claimDateTypes,
  orderDateTypes,
  filterSort,
  YEAR_TO_DATE,
  DEFAULT_ENROLLMENT_DATE_TYPE_FILTER,
  DEFAULT_CLAIM_DATE_TYPE_FILTER,
  DEFAULT_ORDER_DATE_TYPE_FILTER,
  ColumnsKeys
} from "../constant/filters";
import { ReportColumns } from "../component/Reports/ReportColumns";
import { useDispatch, useSelector } from "react-redux";
import {
  selectProvidersFacility,
  selectFilters,
  selectAssigneesAndUserGroups,
  selectMultiples,
  selectIsFetching
} from "../store/selector";
import { DateRangeFilter } from "../component/DateRangeFilter";
import { MultiSelect, SingleSelect } from "../component/CustomSelect";
const { Option } = Select;
import ACTIONS from "../store/action";
import { GRAY, GRAY_500, SOFT_BLUE, PRIMARY_600, GRAY_50 } from "../constant/colors";
import { ApiNames } from "../api/api";
import { fontWeights, sizes } from "../constant/styles";
import styled from "styled-components";
import "./GeneralReport.css";
import ExportButton from "../component/Reports/ExportButton";
import { ENROLLMENTS_TYPE, CLAIMS_TYPE, ORDERS_TYPE, COMPLETED_ORDERS_TYPE } from "../constant/filters";
import { formatAssignee } from "../constant/formaters";
import { ColumnSelector } from "../component/Reports/ColumnSelector";
import Icon from "@ant-design/icons/lib/components/Icon";

const { Text, Title } = Typography;
const { Panel } = Collapse;

const StyledText = styled(Text)`
  color: ${GRAY} !important;
`;

const StyledLoadingText = styled.div`
  color: ${GRAY} !important;
  font-size: ${sizes.small};
  font-weight: ${fontWeights.regular};
`;

const StyledPHINote = styled(Text)`
  display: block;
  color: ${GRAY};
  font-size: 12px;
  text-align: center;
`;

const StyledCollapse = styled(Collapse)`
  background: white;
  z-index: 0;
  border: none !important;
  flex-wrap: wrap;
  .ant-collapse-header {
    width: 156px;
    display: flex;
    padding: 0px 16px !important;
    margin-top: 25px !important;
    justify-content: space-between;
    border: none !important;
    background: white;
    color: ${PRIMARY_600} !important;
    font-weight: ${fontWeights.semibold};
    font-size: ${sizes.medium};
  }

  .ant-collapse-item {
    border: none !important;
  }

  .ant-collapse-content {
    border: none !important;
    background: ${GRAY_50};
    display: none;
  }

  .ant-collapse-item > .ant-collapse-header .ant-collapse-arrow {
    font-size: 0;
    width: 10px;
    height: 10px;
    border-style: solid;
    border-width: 2px 2px 0 0;
    top: 44%;

    transform: ${(props) =>
      props.collapsed ? `rotate(134deg) translate(-1px, 6px)` : "rotate(315deg) translate(0px, 0px)"} !important;
  }

  .ant-menu {
    background: ${GRAY_50};
  }
`;

const GeneralReport = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { trackDropdownSelection } = useGeneralReportTrackingEvent();
  const facilities = useSelector(selectProvidersFacility);
  const assigneesAndGroups = useSelector(selectAssigneesAndUserGroups);
  const filters = useSelector(selectFilters);
  const isLoading = useSelector(
    selectIsFetching([
      ApiNames.InsightsEnrollmentsReport,
      ApiNames.InsightsEnrollmentClaims,
      ApiNames.InsightsClaims,
      ApiNames.InsightsOrders,
      ApiNames.InsightsCompletedOrders
    ])
  );
  const setFiltersAct = (filters) => dispatch(ACTIONS.setFilters(filters));
  const setMultiplesFilter = (multiples) => dispatch(ACTIONS.setMultiplesFilter(multiples));
  const multiples = useSelector(selectMultiples);
  const isExportEnabled =
    filters.enrollmentType ||
    filters.enrollmentClaimsType ||
    filters.claimType ||
    filters.orderType ||
    filters.completedOrderType;
  const selectedDataTypes = [
    { [ENROLLMENTS_TYPE]: filters.enrollmentType },
    { [ENROLLMENT_CLAIMS_TYPE]: filters.enrollmentClaimsType },
    { [CLAIMS_TYPE]: filters.claimType },
    { [ORDERS_TYPE]: filters.orderType },
    { [COMPLETED_ORDERS_TYPE]: filters.completedOrderType }
  ];
  const [assigneesSearchInput, setAssigneesSearchInput] = useState("");
  const [facilitiesSearchInput, setFacilitiesSearchInput] = useState("");
  const [enrollmentCollapsed, setEnrollmentCollapsed] = useState(true);
  const [enrollmentActiveKey, setEnrollmentActiveKey] = useState(["0"]);
  const [enrollmentClaimsCollapsed, setEnrollmentClaimsCollapsed] = useState(true);
  const [enrollmentClaimsActiveKey, setEnrollmentClaimsActiveKey] = useState(["0"]);
  const [claimCollapsed, setClaimCollapsed] = useState(true);
  const [claimActiveKey, setClaimActiveKey] = useState(["0"]);
  const [orderCollapsed, setOrderCollapsed] = useState(true);
  const [orderActiveKey, setOrderActiveKey] = useState(["0"]);
  const [completedOrderCollapsed, setCompletedOrderCollapsed] = useState(true);
  const [completedOrderActiveKey, setCompletedOrderActiveKey] = useState(["0"]);

  // Track default selections on component mount
  useEffect(() => {
    // Track default dropdown selections
    trackDropdownSelection("enrollmentDateTypeFilter", filters.enrollmentDateTypeFilter, "dropdown_default");
  }, []);

  // Restore saved column preferences from localStorage on mount
  useEffect(() => {
    const restoredColumns = { ...ReportColumns };
    let hasChanges = false;

    Object.entries(ColumnsKeys.reports).forEach(([reportKey, storageKey]) => {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const savedColumns = JSON.parse(saved);
          const defaultColumns = ReportColumns[reportKey];
          if (defaultColumns) {
            const merged = {};
            Object.entries(defaultColumns).forEach(([colKey, colDef]) => {
              if (savedColumns[colKey] && typeof savedColumns[colKey].display === "boolean") {
                merged[colKey] = { ...colDef, display: colDef.mandatory ? true : savedColumns[colKey].display };
              } else {
                merged[colKey] = { ...colDef };
              }
            });
            restoredColumns[reportKey] = merged;
            hasChanges = true;
          }
        }
      } catch (err) {
        // Ignore corrupt localStorage data
      }
    });

    if (hasChanges) {
      dispatch(ACTIONS.setReportsColumns(restoredColumns));
    }
  }, []);

  const handleInputDate = (fromDate, toDate, filterName, value) => {
    setFiltersAct({
      ...filters,
      [filterName]: { fromDate, toDate, value }
    });
  };

  const handleInputMultiSelect = (values, filterName, options) => {
    const filterValues = [];
    options.map((option, index) => {
      filterValues[index] = { value: option.value, text: option.children };
    });
    setFiltersAct({
      ...filters,
      [filterName]: values
    });

    setMultiplesFilter({
      ...multiples,
      [filterName]: filterValues
    });
  };

  const handleInputSingleSelect = (value, filterName) => {
    // Track dropdown selection using common utility for pendo event
    trackDropdownSelection(filterName, value);

    setFiltersAct({
      ...filters,
      [filterName]: value
    });
  };

  const handleDataTypeCheckbox = (type, e) => {
    // Track default option selection when data type checkbox is checked for pendo event
    trackDropdownSelection(type, e.target.checked ? "enabled" : "disabled", "checkbox");

    setFiltersAct({
      ...filters,
      [type]: e.target.checked
    });
  };

  const onEnrollmentCollapseChange = (key) => {
    setEnrollmentActiveKey(key);
    setEnrollmentCollapsed(key.length === 1);
  };

  const onEnrollmentClaimsCollapseChange = (key) => {
    setEnrollmentClaimsActiveKey(key);
    setEnrollmentClaimsCollapsed(key.length === 1);
  };

  const onClaimCollapseChange = (key) => {
    setClaimActiveKey(key);
    setClaimCollapsed(key.length === 1);
  };

  const onOrderCollapseChange = (key) => {
    setOrderActiveKey(key);
    setOrderCollapsed(key.length === 1);
  };

  const onCompletedOrderCollapseChange = (key) => {
    setCompletedOrderActiveKey(key);
    setCompletedOrderCollapsed(key.length === 1);
  };

  return (
    <Space
      size={15}
      style={{ margin: "24px", background: "white", height: "100%", padding: "20px" }}
      direction="vertical"
    >
      <Space size={2} direction="vertical" style={{ width: "100%" }}>
        <div>
          <Title
            style={{
              fontSize: "20px",
              fontWeight: fontWeights.semibold,
              background: SOFT_BLUE,
              color: "white",
              padding: "10px 10px 10px 20px"
            }}
          >
            {t("main_title")}
          </Title>
        </div>
        <Text style={{ display: "block", width: "100%", fontWeight: fontWeights.regular, color: GRAY_500 }}>
          {t("sub_title")}
        </Text>
      </Space>
      <div style={{ height: "2px", marginTop: "0px", background: SOFT_BLUE }} />
      <div style={{ marginTop: "10px" }}>
        <Title style={{ fontSize: "16px", fontWeight: fontWeights.semibold }}>{t("data_types.title")}</Title>
        <Text style={{ display: "block", width: "600px", fontWeight: fontWeights.regular, color: GRAY_500 }}>
          {t("data_types.sub_title")}
        </Text>
      </div>
      <Space style={{ marginTop: "15px", width: "100%" }} size={30} direction="vertical">
        <Space style={{ width: "100%" }} direction="vertical">
          <div style={{ display: "flex", alignItems: "start" }}>
            <Checkbox
              id="enrollments-data-type"
              checked={filters.enrollmentType}
              onChange={(e) => handleDataTypeCheckbox("enrollmentType", e)}
              style={{ marginRight: "8px" }}
              className="custom-checkbox"
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: "bold", fontSize: "14px" }}>{t("data_types.enrollments_type")}</div>
              <div style={{ fontSize: "14px", color: GRAY }}>{t("data_types.enrollment_desc")}</div>
              <div style={{ fontSize: "14px", color: GRAY }}>
                {filters.enrollmentType ? t("data_types.enrollment_filters_desc") : ""}
              </div>
            </div>
          </div>
          {filters.enrollmentType && (
            <div>
              <Space style={{ marginLeft: "24px" }} direction={"horizontal"} size={20}>
                <Space size={6} style={{ width: "100%", minWidth: "170px" }} direction="vertical">
                  <StyledText strong>{t("filters.date_type")}</StyledText>
                  <SingleSelect
                    id="enrollment_date_type_filter"
                    className={
                      filters.enrollmentDateTypeFilter === DEFAULT_ENROLLMENT_DATE_TYPE_FILTER ? "date-type-filter" : ""
                    }
                    onChange={(value) => handleInputSingleSelect(value, "enrollmentDateTypeFilter")}
                    value={filters.enrollmentDateTypeFilter}
                    showSearch={false}
                    style={{ width: "100%" }}
                    bordered={true}
                    placeholder={`${t("filters.creation_date_type")}`}
                  >
                    {enrollmentDateTypes.map(({ key, value, translationText }) => (
                      <Option key={key} value={value} id={`enrollment-date-type-${value}`}>
                        {t(translationText)}
                      </Option>
                    ))}
                  </SingleSelect>
                </Space>
                <Space size={6} style={{ width: "100%", minWidth: "170px" }} direction="vertical">
                  <StyledText strong>{t("filters.date_range")}</StyledText>
                  <DateRangeFilter
                    id="date_filter"
                    onSelect={(fromDate, toDate, value = null) =>
                      handleInputDate(fromDate, toDate, "timeRangeFilter", value)
                    }
                    defaultValue={YEAR_TO_DATE}
                    placeHolder={t(YEAR_TO_DATE)}
                    timeRange={filters.timeRangeFilter}
                    bordered={true}
                    onClear={() => {
                      handleInputDate(null, null, "timeRangeFilter", YEAR_TO_DATE);
                    }}
                    allowClear={true}
                  />
                </Space>

                <Space size={6} style={{ width: "215px" }} direction="vertical">
                  <StyledText strong>{t("filters.users")}</StyledText>
                  <MultiSelect
                    id="filter_user"
                    onChange={(values, options) => handleInputMultiSelect(values, "users", options)}
                    value={filters.users}
                    autoClearSearchValue={false}
                    showSearch
                    mode="multiple"
                    maxTagCount={1}
                    maxTagTextLength={filters.users.length > 1 ? 6 : 14}
                    isSelected={filters.users.length > 0}
                    allowClear
                    style={{ width: "100%" }}
                    placeholder={`${t("filters.placeholder.search_users")}`}
                    filterOption={(input, option) => {
                      setAssigneesSearchInput(input.toLowerCase());
                      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    filterSort={filterSort}
                    tooltipContent={multiples.users}
                    bordered={true}
                    clearAll={true}
                    selectAll={true}
                    onClearAll={() => {
                      setAssigneesSearchInput("");
                      handleInputMultiSelect([], "users", []);
                    }}
                    onSelectAll={() => {
                      const filteredAssignees = assigneesAndGroups.filter((d) => {
                        const fullName = formatAssignee(d)?.toLowerCase();
                        return fullName.includes(assigneesSearchInput);
                      });
                      const values = filteredAssignees.map(({ id }) => id);
                      const options = filteredAssignees.map(({ id, firstName, lastName, name, type }, index) => ({
                        value: id,
                        key: index,
                        children: formatAssignee({ firstName, lastName, name, type })
                      }));

                      handleInputMultiSelect(values, "users", options);
                    }}
                  >
                    {assigneesAndGroups &&
                      assigneesAndGroups.map(({ id, firstName, lastName, name, type }) => (
                        <Option key={id} value={id} localSortKey={lastName || name}>
                          {formatAssignee({ firstName, lastName, name, type })}
                        </Option>
                      ))}
                  </MultiSelect>
                </Space>

                <Space size={6} style={{ width: "210px" }} direction="vertical">
                  <StyledText strong>{t("filters.facilities")}</StyledText>
                  <MultiSelect
                    id="filter_patient_facility"
                    onChange={(values, options) => handleInputMultiSelect(values, "facilities", options)}
                    value={filters.facilities}
                    autoClearSearchValue={false}
                    showSearch
                    mode="multiple"
                    maxTagCount={1}
                    tooltipWidth={"24vw"}
                    maxTagTextLength={filters.facilities.length > 1 ? 8 : 14}
                    allowClear
                    isSelected={filters.facilities.length > 0}
                    tooltipContent={multiples.facilities}
                    style={{ width: "100%" }}
                    placeholder={`${t("filters.placeholder.search_facilities")}`}
                    filterOption={(input, option) => {
                      setFacilitiesSearchInput(input.toLowerCase());
                      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    filterSort={filterSort}
                    bordered={true}
                    clearAll={true}
                    selectAll={true}
                    onClearAll={() => {
                      setFacilitiesSearchInput("");
                      handleInputMultiSelect([], "facilities", []);
                    }}
                    onSelectAll={() => {
                      const filteredFacilities = facilities.filter((d) => {
                        return d.name?.toLowerCase().includes(facilitiesSearchInput);
                      });

                      const values = filteredFacilities.map(({ id }) => id);
                      const options = filteredFacilities.map(({ name, id }, index) => ({
                        value: id,
                        key: index,
                        children: name
                      }));

                      handleInputMultiSelect(values, "facilities", options);
                    }}
                  >
                    {facilities &&
                      !!facilities.length &&
                      _.uniqBy(facilities, (facility) => facility.name?.toLowerCase().trim()).map((facility, index) => (
                        <Option key={index} value={facility.id} localSortKey={facility.name}>
                          {facility.name}
                        </Option>
                      ))}
                  </MultiSelect>
                </Space>
                <StyledCollapse
                  expandIconPosition="end"
                  activeKey={enrollmentActiveKey}
                  onChange={onEnrollmentCollapseChange}
                  collapsed={enrollmentCollapsed}
                  defaultActiveKey={["1"]}
                >
                  <Panel
                    header={
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span id="enrollment-show-hide-columns" style={{ fontSize: 14, fontWeight: 600 }}>
                          {enrollmentCollapsed ? t("show_columns") : t("hide_columns")}
                        </span>{" "}
                        <Icon
                          type="left"
                          style={{
                            fontSize: 16,
                            transform: "rotate(90deg)",
                            transition: "transform 0.3s",
                            position: "relative",
                            top: -2
                          }}
                        />
                      </div>
                    }
                    key="1"
                  ></Panel>
                </StyledCollapse>
              </Space>
              {!enrollmentCollapsed && <ColumnSelector selectedReport={"enrollments"} />}
            </div>
          )}
        </Space>
        <Space style={{ width: "100%" }} direction="vertical">
          <div style={{ display: "flex", alignItems: "start" }}>
            <Checkbox
              id="enrollment-claims-data-type"
              checked={filters.enrollmentClaimsType}
              onChange={(e) => handleDataTypeCheckbox("enrollmentClaimsType", e)}
              style={{ marginRight: "8px" }}
              className="custom-checkbox"
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: "bold", fontSize: "14px" }}>{t("data_types.claims_per_enrollment_type")}</div>
              <div style={{ fontSize: "14px", color: GRAY }}>{t("data_types.claims_per_enrollment_desc")}</div>
              <div style={{ fontSize: "14px", color: GRAY }}>
                {filters.enrollmentClaimsType ? t("data_types.claims_per_enrollment_filters_desc") : ""}
              </div>
            </div>
          </div>
          {filters.enrollmentClaimsType && (
            <div>
              <Space style={{ marginLeft: "24px" }} direction={"horizontal"} size={20}>
                <Space size={6} style={{ width: "100%", minWidth: "170px" }} direction="vertical">
                  <StyledText strong>{t("filters.date_range")}</StyledText>
                  <DateRangeFilter
                    id="enrollment-claim_date_filter"
                    onSelect={(fromDate, toDate, value = null) =>
                      handleInputDate(fromDate, toDate, "enrollmentClaimsTimeRangeFilter", value)
                    }
                    defaultValue={YEAR_TO_DATE}
                    placeHolder={t(YEAR_TO_DATE)}
                    timeRange={filters.enrollmentClaimsTimeRangeFilter}
                    bordered={true}
                    onClear={() => {
                      handleInputDate(null, null, "enrollmentClaimsTimeRangeFilter", YEAR_TO_DATE);
                    }}
                    allowClear={true}
                  />
                </Space>

                <Space size={6} style={{ width: "215px" }} direction="vertical">
                  <StyledText strong>{t("filters.users")}</StyledText>
                  <MultiSelect
                    id="enrollment-claim_filter_user"
                    onChange={(values, options) => handleInputMultiSelect(values, "enrollmentClaimsUsers", options)}
                    value={filters.enrollmentClaimsUsers}
                    autoClearSearchValue={false}
                    showSearch
                    mode="multiple"
                    maxTagCount={1}
                    maxTagTextLength={filters.enrollmentClaimsUsers.length > 1 ? 6 : 14}
                    isSelected={filters.enrollmentClaimsUsers.length > 0}
                    allowClear
                    style={{ width: "100%" }}
                    placeholder={`${t("filters.placeholder.search_users")}`}
                    filterOption={(input, option) => {
                      setAssigneesSearchInput(input.toLowerCase());
                      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    filterSort={filterSort}
                    tooltipContent={multiples.enrollmentClaimsUsers}
                    bordered={true}
                    clearAll={true}
                    selectAll={true}
                    onClearAll={() => {
                      setAssigneesSearchInput("");
                      handleInputMultiSelect([], "enrollmentClaimsUsers", []);
                    }}
                    onSelectAll={() => {
                      const filteredAssignees = assigneesAndGroups.filter((d) => {
                        const fullName = formatAssignee(d)?.toLowerCase();
                        return fullName.includes(assigneesSearchInput);
                      });
                      const values = filteredAssignees.map(({ id }) => id);
                      const options = filteredAssignees.map(({ id, firstName, lastName, name, type }, index) => ({
                        value: id,
                        key: index,
                        children: formatAssignee({ firstName, lastName, name, type })
                      }));

                      handleInputMultiSelect(values, "enrollmentClaimsUsers", options);
                    }}
                  >
                    {assigneesAndGroups &&
                      assigneesAndGroups.map(({ id, firstName, lastName, name, type }) => (
                        <Option key={id} value={id} localSortKey={lastName || name}>
                          {formatAssignee({ firstName, lastName, name, type })}
                        </Option>
                      ))}
                  </MultiSelect>
                </Space>

                <Space size={6} style={{ width: "210px" }} direction="vertical">
                  <StyledText strong>{t("filters.facilities")}</StyledText>
                  <MultiSelect
                    id="enrollment_claims_filter_patient_facility"
                    onChange={(values, options) =>
                      handleInputMultiSelect(values, "enrollmentClaimsFacilities", options)
                    }
                    value={filters.enrollmentClaimsFacilities}
                    autoClearSearchValue={false}
                    showSearch
                    mode="multiple"
                    maxTagCount={1}
                    tooltipWidth={"24vw"}
                    maxTagTextLength={filters.enrollmentClaimsFacilities.length > 1 ? 8 : 14}
                    allowClear
                    isSelected={filters.enrollmentClaimsFacilities.length > 0}
                    tooltipContent={multiples.enrollmentClaimsFacilities}
                    style={{ width: "100%" }}
                    placeholder={`${t("filters.placeholder.search_facilities")}`}
                    filterOption={(input, option) => {
                      setFacilitiesSearchInput(input.toLowerCase());
                      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    filterSort={filterSort}
                    bordered={true}
                    clearAll={true}
                    selectAll={true}
                    onClearAll={() => {
                      setFacilitiesSearchInput("");
                      handleInputMultiSelect([], "enrollmentClaimsFacilities", []);
                    }}
                    onSelectAll={() => {
                      const filteredFacilities = facilities.filter((d) => {
                        return d.name?.toLowerCase().includes(facilitiesSearchInput);
                      });

                      const values = filteredFacilities.map(({ id }) => id);
                      const options = filteredFacilities.map(({ name, id }, index) => ({
                        value: id,
                        key: index,
                        children: name
                      }));

                      handleInputMultiSelect(values, "enrollmentClaimsFacilities", options);
                    }}
                  >
                    {facilities &&
                      !!facilities.length &&
                      _.uniqBy(facilities, (facility) => facility.name?.toLowerCase().trim()).map((facility, index) => (
                        <Option key={index} value={facility.name} localSortKey={facility.name}>
                          {facility.name}
                        </Option>
                      ))}
                  </MultiSelect>
                </Space>
                <StyledCollapse
                  expandIconPosition="end"
                  activeKey={enrollmentClaimsActiveKey}
                  onChange={onEnrollmentClaimsCollapseChange}
                  collapsed={enrollmentClaimsCollapsed}
                  defaultActiveKey={["1"]}
                >
                  <Panel
                    header={
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span id="enrollment-claims-show-hide-columns" style={{ fontSize: 14, fontWeight: 600 }}>
                          {enrollmentClaimsCollapsed ? t("show_columns") : t("hide_columns")}
                        </span>{" "}
                        <Icon
                          type="left"
                          style={{
                            fontSize: 16,
                            transform: "rotate(90deg)",
                            transition: "transform 0.3s",
                            position: "relative",
                            top: -3
                          }}
                        />
                      </div>
                    }
                    key="1"
                  ></Panel>
                </StyledCollapse>
              </Space>
              {!enrollmentClaimsCollapsed && <ColumnSelector selectedReport={"enrollment_claims"} />}
            </div>
          )}
        </Space>
        <Space style={{ width: "100%" }} direction="vertical">
          <div style={{ display: "flex", alignItems: "start" }}>
            <Checkbox
              id="claims-data-type"
              checked={filters.claimType}
              onChange={(e) => handleDataTypeCheckbox("claimType", e)}
              style={{ marginRight: "8px" }}
              className="custom-checkbox"
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: "bold", fontSize: "14px" }}>{t("data_types.claim_type")}</div>
              <div style={{ fontSize: "14px", color: GRAY }}>{t("data_types.claim_desc")}</div>
              <div style={{ fontSize: "14px", color: GRAY }}>
                {filters.claimType ? t("data_types.claims_filters_desc") : ""}
              </div>
            </div>
          </div>
          {filters.claimType && (
            <div>
              <Space style={{ marginLeft: "24px" }} direction={"horizontal"} size={20}>
                <Space size={6} style={{ width: "100%", minWidth: "170px" }} direction="vertical">
                  <StyledText strong>{t("filters.date_type")}</StyledText>
                  <SingleSelect
                    id="claim_date_type_filter"
                    className={filters.claimDateTypeFilter === DEFAULT_CLAIM_DATE_TYPE_FILTER ? "date-type-filter" : ""}
                    onChange={(value) => handleInputSingleSelect(value, "claimDateTypeFilter")}
                    value={filters.claimDateTypeFilter}
                    showSearch={false}
                    style={{ width: "100%" }}
                    bordered={true}
                    placeholder={`${t("filters.claim_date_type")}`}
                  >
                    {claimDateTypes.map(({ key, value, translationText }) => (
                      <Option key={key} value={value} id={`claim-date-option-${value}`}>
                        {t(translationText)}
                      </Option>
                    ))}
                  </SingleSelect>
                </Space>
                <Space size={6} style={{ width: "100%", minWidth: "170px" }} direction="vertical">
                  <StyledText strong>{t("filters.date_range")}</StyledText>
                  <DateRangeFilter
                    id="claim_date_filter"
                    onSelect={(fromDate, toDate, value = null) =>
                      handleInputDate(fromDate, toDate, "claimTimeRangeFilter", value)
                    }
                    defaultValue={YEAR_TO_DATE}
                    placeHolder={t(YEAR_TO_DATE)}
                    timeRange={filters.claimTimeRangeFilter}
                    bordered={true}
                    onClear={() => {
                      handleInputDate(null, null, "claimTimeRangeFilter", YEAR_TO_DATE);
                    }}
                    allowClear={true}
                  />
                </Space>

                <Space size={6} style={{ width: "215px" }} direction="vertical">
                  <StyledText strong>{t("filters.users")}</StyledText>
                  <MultiSelect
                    id="claim_filter_user"
                    onChange={(values, options) => handleInputMultiSelect(values, "claimUsers", options)}
                    value={filters.claimUsers}
                    autoClearSearchValue={false}
                    showSearch
                    mode="multiple"
                    maxTagCount={1}
                    maxTagTextLength={filters.claimUsers.length > 1 ? 6 : 14}
                    isSelected={filters.claimUsers.length > 0}
                    allowClear
                    style={{ width: "100%" }}
                    placeholder={`${t("filters.placeholder.search_users")}`}
                    filterOption={(input, option) => {
                      setAssigneesSearchInput(input.toLowerCase());
                      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    filterSort={filterSort}
                    tooltipContent={multiples.claimUsers}
                    bordered={true}
                    clearAll={true}
                    selectAll={true}
                    onClearAll={() => {
                      setAssigneesSearchInput("");
                      handleInputMultiSelect([], "claimUsers", []);
                    }}
                    onSelectAll={() => {
                      const filteredAssignees = assigneesAndGroups.filter((d) => {
                        const fullName = formatAssignee(d)?.toLowerCase();
                        return fullName.includes(assigneesSearchInput);
                      });
                      const values = filteredAssignees.map(({ id }) => id);
                      const options = filteredAssignees.map(({ id, firstName, lastName, name, type }, index) => ({
                        value: id,
                        key: index,
                        children: formatAssignee({ firstName, lastName, name, type })
                      }));

                      handleInputMultiSelect(values, "claimUsers", options);
                    }}
                  >
                    {assigneesAndGroups &&
                      assigneesAndGroups.map(({ id, firstName, lastName, name, type }) => (
                        <Option key={id} value={id} localSortKey={lastName || name}>
                          {formatAssignee({ firstName, lastName, name, type })}
                        </Option>
                      ))}
                  </MultiSelect>
                </Space>

                <Space size={6} style={{ width: "210px" }} direction="vertical">
                  <StyledText strong>{t("filters.facilities")}</StyledText>
                  <MultiSelect
                    id="claim_filter_patient_facility"
                    onChange={(values, options) => handleInputMultiSelect(values, "claimFacilities", options)}
                    value={filters.claimFacilities}
                    autoClearSearchValue={false}
                    showSearch
                    mode="multiple"
                    maxTagCount={1}
                    tooltipWidth={"24vw"}
                    maxTagTextLength={filters.claimFacilities.length > 1 ? 8 : 14}
                    allowClear
                    isSelected={filters.claimFacilities.length > 0}
                    tooltipContent={multiples.claimFacilities}
                    style={{ width: "100%" }}
                    placeholder={`${t("filters.placeholder.search_facilities")}`}
                    filterOption={(input, option) => {
                      setFacilitiesSearchInput(input.toLowerCase());
                      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    filterSort={filterSort}
                    bordered={true}
                    clearAll={true}
                    selectAll={true}
                    onClearAll={() => {
                      setFacilitiesSearchInput("");
                      handleInputMultiSelect([], "claimFacilities", []);
                    }}
                    onSelectAll={() => {
                      const filteredFacilities = facilities.filter((d) => {
                        return d.name?.toLowerCase().includes(facilitiesSearchInput);
                      });

                      const values = filteredFacilities.map(({ id }) => id);
                      const options = filteredFacilities.map(({ name, id }, index) => ({
                        value: id,
                        key: index,
                        children: name
                      }));

                      handleInputMultiSelect(values, "claimFacilities", options);
                    }}
                  >
                    {facilities &&
                      !!facilities.length &&
                      _.uniqBy(facilities, (facility) => facility.name?.toLowerCase().trim()).map((facility, index) => (
                        <Option key={index} value={facility.id} localSortKey={facility.name}>
                          {facility.name}
                        </Option>
                      ))}
                  </MultiSelect>
                </Space>
                <StyledCollapse
                  expandIconPosition="end"
                  activeKey={claimActiveKey}
                  onChange={onClaimCollapseChange}
                  collapsed={claimCollapsed}
                  defaultActiveKey={["1"]}
                >
                  <Panel
                    header={
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span id="claims-show-hide-columns" style={{ fontSize: 14, fontWeight: 600 }}>
                          {claimCollapsed ? t("show_columns") : t("hide_columns")}
                        </span>{" "}
                        <Icon
                          type="left"
                          style={{
                            fontSize: 16,
                            transform: "rotate(90deg)",
                            transition: "transform 0.3s",
                            position: "relative",
                            top: -2
                          }}
                        />
                      </div>
                    }
                    key="1"
                  ></Panel>
                </StyledCollapse>
              </Space>
              {!claimCollapsed && (
                <>
                  <ColumnSelector selectedReport={"claims"} />
                  <ColumnSelector selectedReport={"claim_line_items"} />
                </>
              )}
            </div>
          )}
        </Space>
        <Space style={{ width: "100%" }} direction="vertical">
          <div style={{ display: "flex", alignItems: "start" }}>
            <Checkbox
              id="orders-data-type"
              checked={filters.orderType}
              onChange={(e) => handleDataTypeCheckbox("orderType", e)}
              style={{ marginRight: "8px" }}
              className="custom-checkbox"
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: "bold", fontSize: "14px" }}>{t("data_types.order_type")}</div>
              <div style={{ fontSize: "14px", color: GRAY }}>{t("data_types.order_desc")}</div>
              <div style={{ fontSize: "14px", color: GRAY }}>
                {filters.orderType ? t("data_types.order_filters_desc") : ""}
              </div>
            </div>
          </div>
          {filters.orderType && (
            <div>
              <Space style={{ marginLeft: "24px" }} direction={"horizontal"} size={20}>
                <Space size={6} style={{ width: "100%", minWidth: "170px" }} direction="vertical">
                  <StyledText strong>{t("filters.date_type")}</StyledText>
                  <SingleSelect
                    id="order_date_type_filter"
                    className={filters.orderDateTypeFilter === DEFAULT_ORDER_DATE_TYPE_FILTER ? "date-type-filter" : ""}
                    onChange={(value) => handleInputSingleSelect(value, "orderDateTypeFilter")}
                    value={filters.orderDateTypeFilter}
                    showSearch={false}
                    style={{ width: "100%" }}
                    bordered={true}
                    placeholder={`${t("filters.order_date_type")}`}
                  >
                    {orderDateTypes.map(({ key, value, translationText }) => (
                      <Option key={key} value={value} id={`order-date-type-${value}`}>
                        {t(translationText)}
                      </Option>
                    ))}
                  </SingleSelect>
                </Space>
                <Space size={6} style={{ width: "100%", minWidth: "170px" }} direction="vertical">
                  <StyledText strong>{t("filters.date_range")}</StyledText>
                  <DateRangeFilter
                    id="order_date_filter"
                    onSelect={(fromDate, toDate, value = null) =>
                      handleInputDate(fromDate, toDate, "orderTimeRangeFilter", value)
                    }
                    defaultValue={YEAR_TO_DATE}
                    placeHolder={t(YEAR_TO_DATE)}
                    timeRange={filters.orderTimeRangeFilter}
                    bordered={true}
                    onClear={() => {
                      handleInputDate(null, null, "orderTimeRangeFilter", YEAR_TO_DATE);
                    }}
                    allowClear={true}
                  />
                </Space>

                <Space size={6} style={{ width: "215px" }} direction="vertical">
                  <StyledText strong>{t("filters.users")}</StyledText>
                  <MultiSelect
                    id="order_filter_user"
                    onChange={(values, options) => handleInputMultiSelect(values, "orderUsers", options)}
                    value={filters.orderUsers}
                    autoClearSearchValue={false}
                    showSearch
                    mode="multiple"
                    maxTagCount={1}
                    maxTagTextLength={filters.orderUsers.length > 1 ? 6 : 14}
                    isSelected={filters.orderUsers.length > 0}
                    allowClear
                    style={{ width: "100%" }}
                    placeholder={`${t("filters.placeholder.search_users")}`}
                    filterOption={(input, option) => {
                      setAssigneesSearchInput(input.toLowerCase());
                      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    filterSort={filterSort}
                    tooltipContent={multiples.orderUsers}
                    bordered={true}
                    clearAll={true}
                    selectAll={true}
                    onClearAll={() => {
                      setAssigneesSearchInput("");
                      handleInputMultiSelect([], "orderUsers", []);
                    }}
                    onSelectAll={() => {
                      const filteredAssignees = assigneesAndGroups.filter((d) => {
                        const fullName = formatAssignee(d)?.toLowerCase();
                        return fullName.includes(assigneesSearchInput);
                      });
                      const values = filteredAssignees.map(({ id }) => id);
                      const options = filteredAssignees.map(({ id, firstName, lastName, name, type }, index) => ({
                        value: id,
                        key: index,
                        children: formatAssignee({ firstName, lastName, name, type })
                      }));

                      handleInputMultiSelect(values, "orderUsers", options);
                    }}
                  >
                    {assigneesAndGroups &&
                      assigneesAndGroups.map(({ id, firstName, lastName, name, type }) => (
                        <Option key={id} value={id} localSortKey={lastName || name}>
                          {formatAssignee({ firstName, lastName, name, type })}
                        </Option>
                      ))}
                  </MultiSelect>
                </Space>

                <Space size={6} style={{ width: "210px" }} direction="vertical">
                  <StyledText strong>{t("filters.facilities")}</StyledText>
                  <MultiSelect
                    id="order_filter_patient_facility"
                    onChange={(values, options) => handleInputMultiSelect(values, "orderFacilities", options)}
                    value={filters.orderFacilities}
                    autoClearSearchValue={false}
                    showSearch
                    mode="multiple"
                    maxTagCount={1}
                    tooltipWidth={"24vw"}
                    maxTagTextLength={filters.orderFacilities.length > 1 ? 8 : 14}
                    allowClear
                    isSelected={filters.orderFacilities.length > 0}
                    tooltipContent={multiples.orderFacilities}
                    style={{ width: "100%" }}
                    placeholder={`${t("filters.placeholder.search_facilities")}`}
                    filterOption={(input, option) => {
                      setFacilitiesSearchInput(input.toLowerCase());
                      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    filterSort={filterSort}
                    bordered={true}
                    clearAll={true}
                    selectAll={true}
                    onClearAll={() => {
                      setFacilitiesSearchInput("");
                      handleInputMultiSelect([], "orderFacilities", []);
                    }}
                    onSelectAll={() => {
                      const filteredFacilities = facilities.filter((d) => {
                        return d.name?.toLowerCase().includes(facilitiesSearchInput);
                      });

                      const values = filteredFacilities.map(({ id }) => id);
                      const options = filteredFacilities.map(({ name, id }, index) => ({
                        value: id,
                        key: index,
                        children: name
                      }));

                      handleInputMultiSelect(values, "orderFacilities", options);
                    }}
                  >
                    {facilities &&
                      !!facilities.length &&
                      _.uniqBy(facilities, (facility) => facility.name?.toLowerCase().trim()).map((facility, index) => (
                        <Option key={index} value={facility.id} localSortKey={facility.name}>
                          {facility.name}
                        </Option>
                      ))}
                  </MultiSelect>
                </Space>
                <StyledCollapse
                  expandIconPosition="end"
                  activeKey={orderActiveKey}
                  onChange={onOrderCollapseChange}
                  collapsed={orderCollapsed}
                  defaultActiveKey={["1"]}
                >
                  <Panel
                    header={
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span id="orders-show-hide-columns" style={{ fontSize: 14, fontWeight: 600 }}>
                          {orderCollapsed ? t("show_columns") : t("hide_columns")}
                        </span>{" "}
                        <Icon
                          type="left"
                          style={{
                            fontSize: 16,
                            transform: "rotate(90deg)",
                            transition: "transform 0.3s",
                            position: "relative",
                            top: -2
                          }}
                        />
                      </div>
                    }
                    key="1"
                  ></Panel>
                </StyledCollapse>
              </Space>
              {!orderCollapsed && (
                <>
                  <ColumnSelector selectedReport={"orders"} />
                  <ColumnSelector selectedReport={"order_line_items"} />
                </>
              )}
            </div>
          )}
        </Space>

        <Space style={{ width: "100%" }} direction="vertical">
          <div style={{ display: "flex", alignItems: "start" }}>
            <Checkbox
              id="completed-orders-data-type"
              checked={filters.completedOrderType}
              onChange={(e) => handleDataTypeCheckbox("completedOrderType", e)}
              style={{ marginRight: "8px" }}
              className="custom-checkbox"
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: "bold", fontSize: "14px" }}>{t("data_types.completed_order_type")}</div>
              <div style={{ fontSize: "14px", color: GRAY }}>{t("data_types.completed_order_desc")}</div>
              <div style={{ fontSize: "14px", color: GRAY }}>
                {filters.completedOrderType ? t("data_types.completed_order_filters_desc") : ""}
              </div>
            </div>
          </div>
          {filters.completedOrderType && (
            <div>
              <Space style={{ marginLeft: "24px" }} direction={"horizontal"} size={20}>
                <Space size={6} style={{ width: "100%", minWidth: "170px" }} direction="vertical">
                  <StyledText strong>{t("filters.date_range")}</StyledText>
                  <DateRangeFilter
                    id="completed_order_date_filter"
                    onSelect={(fromDate, toDate, value = null) =>
                      handleInputDate(fromDate, toDate, "completedOrderTimeRangeFilter", value)
                    }
                    defaultValue={YEAR_TO_DATE}
                    placeHolder={t(YEAR_TO_DATE)}
                    timeRange={filters.completedOrderTimeRangeFilter}
                    bordered={true}
                    onClear={() => {
                      handleInputDate(null, null, "completedOrderTimeRangeFilter", YEAR_TO_DATE);
                    }}
                    allowClear={true}
                  />
                </Space>

                <Space size={6} style={{ width: "215px" }} direction="vertical">
                  <StyledText strong>{t("filters.users")}</StyledText>
                  <MultiSelect
                    id="completed_order_filter_user"
                    onChange={(values, options) => handleInputMultiSelect(values, "completedOrderUsers", options)}
                    value={filters.completedOrderUsers}
                    autoClearSearchValue={false}
                    showSearch
                    mode="multiple"
                    maxTagCount={1}
                    maxTagTextLength={filters.completedOrderUsers.length > 1 ? 6 : 14}
                    isSelected={filters.completedOrderUsers.length > 0}
                    allowClear
                    style={{ width: "100%" }}
                    placeholder={`${t("filters.placeholder.search_users")}`}
                    filterOption={(input, option) => {
                      setAssigneesSearchInput(input.toLowerCase());
                      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    filterSort={filterSort}
                    tooltipContent={multiples.completedOrderUsers}
                    bordered={true}
                    clearAll={true}
                    selectAll={true}
                    onClearAll={() => {
                      setAssigneesSearchInput("");
                      handleInputMultiSelect([], "completedOrderUsers", []);
                    }}
                    onSelectAll={() => {
                      const filteredAssignees = assigneesAndGroups.filter((d) => {
                        const fullName = formatAssignee(d)?.toLowerCase();
                        return fullName.includes(assigneesSearchInput);
                      });
                      const values = filteredAssignees.map(({ id }) => id);
                      const options = filteredAssignees.map(({ id, firstName, lastName, name, type }, index) => ({
                        value: id,
                        key: index,
                        children: formatAssignee({ firstName, lastName, name, type })
                      }));

                      handleInputMultiSelect(values, "completedOrderUsers", options);
                    }}
                  >
                    {assigneesAndGroups &&
                      assigneesAndGroups.map(({ id, firstName, lastName, name, type }) => (
                        <Option key={id} value={id} localSortKey={lastName || name}>
                          {formatAssignee({ firstName, lastName, name, type })}
                        </Option>
                      ))}
                  </MultiSelect>
                </Space>

                <Space size={6} style={{ width: "210px" }} direction="vertical">
                  <StyledText strong>{t("filters.facilities")}</StyledText>
                  <MultiSelect
                    id="completed_order_filter_patient_facility"
                    onChange={(values, options) => handleInputMultiSelect(values, "completedOrderFacilities", options)}
                    value={filters.completedOrderFacilities}
                    autoClearSearchValue={false}
                    showSearch
                    mode="multiple"
                    maxTagCount={1}
                    tooltipWidth={"24vw"}
                    maxTagTextLength={filters.completedOrderFacilities.length > 1 ? 8 : 14}
                    allowClear
                    isSelected={filters.completedOrderFacilities.length > 0}
                    tooltipContent={multiples.completedOrderFacilities}
                    style={{ width: "100%" }}
                    placeholder={`${t("filters.placeholder.search_facilities")}`}
                    filterOption={(input, option) => {
                      setFacilitiesSearchInput(input.toLowerCase());
                      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    filterSort={filterSort}
                    bordered={true}
                    clearAll={true}
                    selectAll={true}
                    onClearAll={() => {
                      setFacilitiesSearchInput("");
                      handleInputMultiSelect([], "completedOrderFacilities", []);
                    }}
                    onSelectAll={() => {
                      const filteredFacilities = facilities.filter((d) => {
                        return d.name?.toLowerCase().includes(facilitiesSearchInput);
                      });

                      const values = filteredFacilities.map(({ id }) => id);
                      const options = filteredFacilities.map(({ name, id }, index) => ({
                        value: id,
                        key: index,
                        children: name
                      }));

                      handleInputMultiSelect(values, "completedOrderFacilities", options);
                    }}
                  >
                    {facilities &&
                      !!facilities.length &&
                      _.uniqBy(facilities, (facility) => facility.name?.toLowerCase().trim()).map((facility, index) => (
                        <Option key={index} value={facility.id} localSortKey={facility.name}>
                          {facility.name}
                        </Option>
                      ))}
                  </MultiSelect>
                </Space>
                <StyledCollapse
                  expandIconPosition="end"
                  activeKey={completedOrderActiveKey}
                  onChange={onCompletedOrderCollapseChange}
                  collapsed={completedOrderCollapsed}
                  defaultActiveKey={["1"]}
                >
                  <Panel
                    header={
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span id="completed-orders-show-hide-columns" style={{ fontSize: 14, fontWeight: 600 }}>
                          {completedOrderCollapsed ? t("show_columns") : t("hide_columns")}
                        </span>{" "}
                        <Icon
                          type="left"
                          style={{
                            fontSize: 16,
                            transform: "rotate(90deg)",
                            transition: "transform 0.3s",
                            position: "relative",
                            top: -2
                          }}
                        />
                      </div>
                    }
                    key="1"
                  ></Panel>
                </StyledCollapse>
              </Space>
              {!completedOrderCollapsed && (
                <>
                  <ColumnSelector selectedReport={"completed_orders"} />
                  <ColumnSelector selectedReport={"order_line_items"} />
                </>
              )}
            </div>
          )}
        </Space>
      </Space>
      <Space size={10} direction="vertical" style={{ marginTop: "20px" }}>
        <Space style={{ marginLeft: "2px" }}>
          <InfoCircleOutlined style={{ color: GRAY }} />
          <StyledPHINote>{t("data_types.export_report_note")}</StyledPHINote>
        </Space>
        <Space direction="horizontal" size={20}>
          <ExportButton
            isLoading={isLoading}
            isExportEnabled={isExportEnabled}
            filters={filters}
            selectedDataTypes={selectedDataTypes}
          />
          {isLoading && <StyledLoadingText>{t("loading_string")}</StyledLoadingText>}
        </Space>
      </Space>
    </Space>
  );
};

export { GeneralReport };
